import React from 'react';
import PublicHeader from './PublicHeader';

interface WaiverPageProps {
  onNavigateHome?: () => void;
  onNavigateToMission?: () => void;
  onNavigateToRules?: () => void;
  onNavigateToConduct?: () => void;
}

const WaiverPage: React.FC<WaiverPageProps> = ({ onNavigateHome, onNavigateToMission, onNavigateToRules, onNavigateToConduct }) => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-blue-100">
      {/* Header */}
      <PublicHeader title="Waiver and Release" />

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
          {/* Title */}
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-fishing-blue mb-2">Tumbulgum Fishing Club Inc.</h2>
            <h3 className="text-2xl font-bold text-gray-800 mt-4">Waiver and Release</h3>
          </div>

          {/* Introduction */}
          <div className="mb-8 space-y-4 text-gray-700 leading-relaxed">
            <p>
              I acknowledge that Tumbulgum Fishing Club Inc. (the Club) prioritises the safety of its members.
            </p>
            <p>
              I understand that participation in Club activities involves personal decisions—such as where to fish, 
              weather conditions, tides, and use of safety equipment—and that I am solely responsible for 
              assessing these risks.
            </p>
          </div>

          {/* Waiver Sections */}
          <div className="space-y-6">
            {/* Assumption of Risk */}
            <div className="border-l-4 border-yellow-500 bg-yellow-50 p-6 rounded-r-lg">
              <div className="flex items-start">
                <span className="text-3xl mr-4">⚠️</span>
                <div>
                  <h4 className="text-xl font-bold text-gray-800 mb-3">Assumption of Risk</h4>
                  <p className="text-gray-700 leading-relaxed">
                    I understand that fishing and related Club activities carry inherent risks, including injury, illness, 
                    property damage, or death. I voluntarily accept full responsibility for these risks and for the actions 
                    of any person under my care.
                  </p>
                </div>
              </div>
            </div>

            {/* Release of Liability */}
            <div className="border-l-4 border-red-500 bg-red-50 p-6 rounded-r-lg">
              <div className="flex items-start">
                <span className="text-3xl mr-4">📝</span>
                <div>
                  <h4 className="text-xl font-bold text-gray-800 mb-3">Release of Liability</h4>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    To the fullest extent permitted by law, I release and discharge the Club, its executive committee, 
                    office bearers, members, volunteers, and agents from any and all claims, demands, or liabilities 
                    arising from:
                  </p>
                  <ul className="list-disc ml-6 space-y-2 text-gray-700">
                    <li>Injury or loss to myself or others under my care</li>
                    <li>Property damage</li>
                    <li>Legal costs or expenses</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Indemnity */}
            <div className="border-l-4 border-blue-500 bg-blue-50 p-6 rounded-r-lg">
              <div className="flex items-start">
                <span className="text-3xl mr-4">🛡️</span>
                <div>
                  <h4 className="text-xl font-bold text-gray-800 mb-3">Indemnity</h4>
                  <p className="text-gray-700 leading-relaxed">
                    I agree to indemnify and hold harmless the Club and its representatives from any claims, 
                    damages, or legal actions brought by third parties as a result of my participation in Club activities.
                  </p>
                </div>
              </div>
            </div>

            {/* Medical Consent */}
            <div className="border-l-4 border-green-500 bg-green-50 p-6 rounded-r-lg">
              <div className="flex items-start">
                <span className="text-3xl mr-4">🚑</span>
                <div>
                  <h4 className="text-xl font-bold text-gray-800 mb-3">Medical Consent</h4>
                  <p className="text-gray-700 leading-relaxed">
                    In an emergency, I authorise the Club or its representatives to seek medical treatment on my 
                    behalf. I accept responsibility for any associated costs.
                  </p>
                </div>
              </div>
            </div>

            {/* Medical Disclosure */}
            <div className="border-l-4 border-purple-500 bg-purple-50 p-6 rounded-r-lg">
              <div className="flex items-start">
                <span className="text-3xl mr-4">🩺</span>
                <div>
                  <h4 className="text-xl font-bold text-gray-800 mb-3">Medical Disclosure</h4>
                  <p className="text-gray-700 leading-relaxed">
                    If I—or someone in my care—has an existing medical condition that may affect safe participation in 
                    a Club event, I will notify a Club officer beforehand confirming appropriate processes are in place to 
                    cover any untoward circumstances. The Club may, at its discretion, decide to check on the Member 
                    or its nominee through the day to ensure all is well.
                  </p>
                </div>
              </div>
            </div>

            {/* Compliance with Rules */}
            <div className="border-l-4 border-indigo-500 bg-indigo-50 p-6 rounded-r-lg">
              <div className="flex items-start">
                <span className="text-3xl mr-4">📘</span>
                <div>
                  <h4 className="text-xl font-bold text-gray-800 mb-3">Compliance with Rules</h4>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    I agree to follow all safety instructions, rules, and directions issued by:
                  </p>
                  <ul className="list-disc ml-6 space-y-2 text-gray-700">
                    <li>The Club and its Executive Committee</li>
                    <li>NSW Maritime and Fishing Authorities</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Parental/Guardian Responsibility */}
            <div className="border-l-4 border-pink-500 bg-pink-50 p-6 rounded-r-lg">
              <div className="flex items-start">
                <span className="text-3xl mr-4">👨‍👩‍👧</span>
                <div>
                  <h4 className="text-xl font-bold text-gray-800 mb-3">Parental/Guardian Responsibility</h4>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    If I am the parent or guardian of a junior participant, I accept full responsibility for their supervision 
                    and safety during Club activities.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Parents / Guardians understand and agree photos / names of Junior Members may appear in Club 
                    social media / Newsletters from time to time and allows this to occur without recourse to the Club, 
                    its Officers or Volunteers.
                  </p>
                </div>
              </div>
            </div>

            {/* Photo and Image Use */}
            <div className="border-l-4 border-purple-500 bg-purple-50 p-6 rounded-r-lg">
              <div className="flex items-start">
                <span className="text-3xl mr-4">📸</span>
                <div>
                  <h4 className="text-xl font-bold text-gray-800 mb-3">Photo and Image Use</h4>
                  <p className="text-gray-700 leading-relaxed">
                    I agree that any photo I submit to the Club — including catch photos, Happy Snaps, and event
                    photos — may be used by the Club for Club purposes, including on the Club's website, social
                    media, and other online channels.
                  </p>
                </div>
              </div>
            </div>

            {/* Executive Protection Clause */}
            <div className="border-l-4 border-gray-500 bg-gray-50 p-6 rounded-r-lg">
              <div className="flex items-start">
                <span className="text-3xl mr-4">🔒</span>
                <div>
                  <h4 className="text-xl font-bold text-gray-800 mb-3">Executive Protection Clause</h4>
                  <p className="text-gray-700 leading-relaxed">
                    I acknowledge that the Club's executive committee and volunteers act in good faith and in a 
                    voluntary capacity. I agree not to pursue legal action against any individual acting on behalf of the 
                    Club in the course of their duties, except in cases of proven gross negligence or unlawful conduct.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Important Notice */}
          <div className="mt-12 p-6 bg-fishing-blue text-white rounded-lg">
            <p className="text-center text-lg font-semibold mb-2">
              Important Notice
            </p>
            <p className="text-center text-sm">
              By participating in Tumbulgum Fishing Club activities, you acknowledge that you have read, 
              understood, and agree to the terms of this waiver and release.
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12 px-4 mt-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-xl font-semibold mb-4">Contact Us</h3>
              <p className="text-gray-300">Website: www.tumbyfc.com</p>
              <p className="text-gray-300">Email: fishing@tumbyfc.com</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li><button onClick={onNavigateHome} className="text-gray-300 hover:text-white">Home</button></li>
                <li><button onClick={onNavigateToMission} className="text-gray-300 hover:text-white">Mission, Values & Vision</button></li>
                <li><button onClick={onNavigateToRules} className="text-gray-300 hover:text-white">Club Rules</button></li>
                <li><button onClick={onNavigateToConduct} className="text-gray-300 hover:text-white">Code of Conduct</button></li>
                <li><a href="/Constitution-Tumbulgum-Fishing-Club-V5.pdf" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white">Club Constitution</a></li>
                <li><button className="text-gray-300 hover:text-white">Privacy Policy</button></li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-4">Location</h3>
              <p className="text-gray-300">Tumbulgum, New South Wales</p>
              <p className="text-gray-300">Australia</p>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-8 pt-8 text-center">
            <p className="text-gray-400">&copy; 2026 Tumbulgum Fishing Club. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default WaiverPage;
